import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { useIsMobile } from "../../hooks/useIsMobile";
import { apiFetch } from "../../utils/api";
import {
  formatPrice,
  formatArea,
  formatPhone,
  trustLabel,
  trustColor,
} from "../../utils/format";
import { usePageTitle } from "../../hooks/usePageTitle";
import { KakaoMapView } from "../../components/KakaoMapView";
import type { ListingResponse } from "../listings/types";

interface NdaStatus {
  agreed: boolean;
  agreedAt?: string;
  name?: string;
}

interface MyListing {
  id: string;
  property_type: string;
  region_sido: string;
  region_sigungu: string;
  region_eupmyeondong: string;
  area_pyeong: number;
  price_manwon: number;
  status: string;
  created_at: string;
  blind_lat?: number;
  blind_lng?: number;
}

interface Inquiry {
  id: string;
  listing_id: string;
  listing_title: string;
  sender_name: string;
  sender_phone: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export function MyPage() {
  usePageTitle("\uB9C8\uC774\uD398\uC774\uC9C0");
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { broker, refresh } = useAuth();
  const [nda, setNda] = useState<NdaStatus | null>(null);
  const [listings, setListings] = useState<MyListing[]>([]);
  const [favorites, setFavorites] = useState<MyListing[]>([]);
  const [loadingNda, setLoadingNda] = useState(true);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loadingInquiries, setLoadingInquiries] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [statusLoadingId, setStatusLoadingId] = useState<string | null>(null);
  const [emailEdit, setEmailEdit] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showMyMap, setShowMyMap] = useState(false);
  const [selectedMapDetail, setSelectedMapDetail] = useState<MyListing | null>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollToContent = useCallback(() => {
    setTimeout(() => contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, []);
  const [filterKeyword, setFilterKeyword] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriceMin, setFilterPriceMin] = useState("");
  const [filterPriceMax, setFilterPriceMax] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [verifyFiles, setVerifyFiles] = useState<string[]>([]);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");

  useEffect(() => {
    refresh();
  }, []);

  // NDA 상태 조회
  useEffect(() => {
    apiFetch<NdaStatus>("/api/nda/status")
      .then(setNda)
      .catch(() => setNda({ agreed: false }))
      .finally(() => setLoadingNda(false));
  }, []);

  // 내 매물 조회 (모든 상태 포함)
  useEffect(() => {
    apiFetch<{ listings: MyListing[] }>("/api/listings/my")
      .then((res) => setListings(res.listings))
      .catch(() => setListings([]))
      .finally(() => setLoadingListings(false));
  }, []);

  // 받은 문의 조회
  useEffect(() => {
    apiFetch<{ inquiries: Inquiry[] }>("/api/inquiries/my")
      .then((res) => setInquiries(res.inquiries))
      .catch(() => setInquiries([]))
      .finally(() => setLoadingInquiries(false));
  }, []);

  const markRead = async (inquiryId: string) => {
    try {
      await apiFetch(`/api/inquiries/${inquiryId}/read`, { method: "PATCH" });
      setInquiries((prev) => prev.map((inq) =>
        inq.id === inquiryId ? { ...inq, is_read: true } : inq
      ));
    } catch { /* ignore */ }
  };

  const handleStatusChange = async (listingId: string, newStatus: string) => {
    setStatusLoadingId(listingId);
    try {
      await apiFetch(`/api/listings/${listingId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      setListings((prev) => prev.map((l) =>
        l.id === listingId ? { ...l, status: newStatus } : l
      ));
    } catch { /* ignore */ } finally {
      setStatusLoadingId(null);
    }
  };

  const handleEmailSave = async () => {
    if (!emailValue.includes("@")) { setEmailMsg("\uC62C\uBC14\uB978 \uC774\uBA54\uC77C\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694"); return; }
    setEmailSaving(true);
    setEmailMsg("");
    try {
      await apiFetch("/api/auth/email", {
        method: "PATCH",
        body: JSON.stringify({ email: emailValue }),
      });
      await refresh();
      setEmailEdit(false);
      setEmailMsg("\uC800\uC7A5\uB418\uC5C8\uC2B5\uB2C8\uB2E4");
    } catch { setEmailMsg("\uC800\uC7A5\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4"); }
    finally { setEmailSaving(false); }
  };

  const handleVerifyFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const MAX_B64 = Math.ceil(2 * 1024 * 1024 * (4 / 3)) + 100;
    const newB64s: string[] = [];
    for (const file of files) {
      if (verifyFiles.length + newB64s.length >= 3) break;
      const b64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      if (b64.length > MAX_B64) {
        setVerifyMsg("\uD30C\uC77C \uD06C\uAE30\uB294 \uC7A5\uB2F9 2MB\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4");
        e.target.value = "";
        return;
      }
      newB64s.push(b64);
    }
    setVerifyFiles((prev) => [...prev, ...newB64s].slice(0, 3));
    e.target.value = "";
  };

  const handleVerifySubmit = async () => {
    if (verifyFiles.length === 0) return;
    setVerifyLoading(true);
    setVerifyMsg("");
    try {
      await apiFetch("/api/auth/verify-request", {
        method: "PATCH",
        body: JSON.stringify({ files: verifyFiles }),
      });
      await refresh();
      setVerifyMsg("\uC11C\uB958\uAC00 \uC81C\uCD9C\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uAD00\uB9AC\uC790 \uAC80\uD1A0 \uD6C4 \uC778\uC99D\uB429\uB2C8\uB2E4.");
      setTimeout(() => {
        setShowVerifyModal(false);
        setVerifyFiles([]);
        setVerifyMsg("");
      }, 2000);
    } catch (err: any) {
      setVerifyMsg(err.message || "\uC81C\uCD9C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4");
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDelete = async (listingId: string) => {
    if (!window.confirm("\uC815\uB9D0 \uC774 \uB9E4\uBB3C\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?\n\uC0AD\uC81C\uD558\uBA74 \uBCF5\uAD6C\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.")) return;
    setDeletingId(listingId);
    try {
      await apiFetch(`/api/listings/${listingId}`, { method: "DELETE" });
      setListings((prev) => prev.filter((l) => l.id !== listingId));
    } catch { /* ignore */ } finally {
      setDeletingId(null);
    }
  };

  // 관심매물 조회
  useEffect(() => {
    apiFetch<{ listings: MyListing[] }>("/api/favorites/details")
      .then((res) => setFavorites(res.listings))
      .catch(() => setFavorites([]))
      .finally(() => setLoadingFavorites(false));
  }, []);

  const filteredListings = useMemo(() => {
    let result = [...listings];
    if (filterKeyword.trim()) {
      const kw = filterKeyword.toLowerCase();
      result = result.filter(l =>
        `${l.region_sido} ${l.region_sigungu} ${l.region_eupmyeondong}`.toLowerCase().includes(kw) ||
        l.property_type.toLowerCase().includes(kw)
      );
    }
    if (filterType) result = result.filter(l => l.property_type === filterType);
    if (filterStatus) result = result.filter(l => l.status === filterStatus);
    if (filterPriceMin) result = result.filter(l => l.price_manwon >= Number(filterPriceMin));
    if (filterPriceMax) result = result.filter(l => l.price_manwon <= Number(filterPriceMax));
    result.sort((a, b) => {
      if (sortBy === "price_asc") return a.price_manwon - b.price_manwon;
      if (sortBy === "price_desc") return b.price_manwon - a.price_manwon;
      if (sortBy === "area_asc") return a.area_pyeong - b.area_pyeong;
      if (sortBy === "area_desc") return b.area_pyeong - a.area_pyeong;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    return result;
  }, [listings, filterKeyword, filterType, filterStatus, filterPriceMin, filterPriceMax, sortBy]);

  const resetFilters = () => {
    setFilterKeyword("");
    setFilterType("");
    setFilterStatus("");
    setFilterPriceMin("");
    setFilterPriceMax("");
    setSortBy("newest");
  };

  const hasActiveFilter = !!(filterKeyword || filterType || filterStatus || filterPriceMin || filterPriceMax || sortBy !== "newest");

  const openDetail = useCallback((id: string) => {
    const found = filteredListings.find((x) => x.id === id);
    if (found) {
      setSelectedMapDetail(found);
      setDetailData(null);
      setDetailLoading(true);
      apiFetch<any>(`/api/listings/${id}`)
        .then(setDetailData)
        .catch((e) => console.warn('listing detail fetch failed:', e))
        .finally(() => setDetailLoading(false));
    }
  }, [filteredListings]);

  const fSelectStyle: React.CSSProperties = {
    padding: "7px 12px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontSize: 16,
    cursor: "pointer",
  };

  if (!broker) {
    return <div style={styles.loading}>불러오는 중...</div>;
  }

  const score = broker.trust_score ?? 50;

  return (
    <div style={styles.page}>
      <AppHeader backTo="/dashboard" backLabel="← 대시보드" />
      <div style={{ ...styles.container, padding: isMobile ? "0 16px 40px" : "0 32px 48px" }}>

        {/* ── Section 1: 내 정보 ──────────────── */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>내 정보</h3>
          <div style={styles.cardBody}>
            {/* 프로필 상단 */}
            <div style={styles.profileRow}>
              <div style={styles.avatar}>{broker.name.charAt(0)}</div>
              <div style={{ flex: 1 }}>
                <h2 style={styles.name}>{broker.name}</h2>
                <p style={styles.officeName}>{broker.office_name}</p>
              </div>
              {(broker.is_verified || broker.verification_status === "approved") ? (
                <span style={styles.verifiedBadge}>{"\u2713 \uC778\uC99D\uC644\uB8CC"}</span>
              ) : broker.verification_status === "pending" ? (
                <span style={styles.pendingBadge}>{"\uC2EC\uC0AC\uC911"}</span>
              ) : broker.verification_status === "rejected" ? (
                <span style={styles.rejectedBadge}>{"\uAC70\uC808\uB428"}</span>
              ) : (
                <span style={styles.unverifiedBadge}>{"\uBBF8\uC2E0\uCCAD"}</span>
              )}
            </div>

            {/* 정보 그리드 */}
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 12,
              marginTop: 16,
            }}>
              <InfoRow label="자격증번호" value={broker.license_number || "-"} />
              <InfoRow label="휴대폰" value={broker.phone ? formatPhone(broker.phone) : "-"} />
              <InfoRow label="활동지역" value={
                [broker.region_sido, broker.region_sigungu].filter(Boolean).join(" ") || "-"
              } />
              <InfoRow label="가입일" value={
                broker.created_at
                  ? new Date(broker.created_at).toLocaleDateString("ko-KR")
                  : "-"
              } />
            </div>

            {/* 신뢰도 */}
            <div style={styles.trustSection}>
              <div style={styles.trustHeader}>
                <span style={styles.trustLabel}>신뢰도</span>
                <span style={{ color: trustColor(score), fontWeight: 700, fontSize: 14 }}>
                  {score}점 ({trustLabel(score)})
                </span>
              </div>
              <div style={styles.progressBg}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${score}%`,
                    background: trustColor(score),
                  }}
                />
              </div>
            </div>

            {/* 통계 */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 10,
              marginTop: 16,
            }}>
              <MiniStat label="등록 매물" value={broker.total_listings ?? 0} />
              <MiniStat label="성사 거래" value={broker.total_deals_closed ?? 0} />
              <MiniStat label="NDA 위반" value={broker.nda_violations ?? 0} />
            </div>

            {/* 비밀번호 변경 */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border-light)" }}>
              <button
                onClick={() => navigate("/change-password")}
                style={{
                  background: "none",
                  border: "1px solid var(--border-medium)",
                  color: "var(--text-secondary)",
                  padding: "8px 16px",
                  borderRadius: "var(--radius-md)",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                {"\uD83D\uDD10 \uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD"}
              </button>
            </div>

            {/* 자격증 인증 */}
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-light)" }}>
              {(broker.is_verified || broker.verification_status === "approved") ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: "var(--radius-md)" }}>
                  <span style={{ color: "var(--lt-green)", fontSize: 16 }}>{"\u2713"}</span>
                  <span style={{ fontSize: 13, color: "var(--lt-green)", fontWeight: 600 }}>{"\uC790\uACA9\uC99D \uC778\uC99D \uC644\uB8CC"}</span>
                </div>
              ) : broker.verification_status === "pending" ? (
                <div style={{ padding: "10px 14px", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: "var(--radius-md)" }}>
                  <p style={{ margin: 0, fontSize: 13, color: "var(--lt-orange)", fontWeight: 600 }}>{"\uC11C\uB958 \uC2EC\uC0AC\uC911"}</p>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-muted)" }}>{"\uAD00\uB9AC\uC790 \uAC80\uD1A0 \uD6C4 \uC778\uC99D \uC2B9\uC778\uB429\uB2C8\uB2E4"}</p>
                </div>
              ) : broker.verification_status === "rejected" ? (
                <div style={{ padding: "10px 14px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "var(--radius-md)" }}>
                  <p style={{ margin: 0, fontSize: 13, color: "var(--lt-red)", fontWeight: 600 }}>{"\uC778\uC99D \uAC70\uC808"}</p>
                  {broker.verification_reject_reason && (
                    <p style={{ margin: "4px 0 8px", fontSize: 12, color: "var(--text-muted)" }}>{"\uAC70\uC808 \uC0AC\uC720: "}{broker.verification_reject_reason}</p>
                  )}
                  <button onClick={() => setShowVerifyModal(true)} style={{ background: "none", border: "1px solid var(--lt-red)", color: "var(--lt-red)", padding: "6px 14px", borderRadius: "var(--radius-md)", fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>
                    {"\uC7AC\uC2E0\uCCAD"}
                  </button>
                </div>
              ) : (
                <button onClick={() => setShowVerifyModal(true)} style={{ background: "none", border: "1px solid var(--lt-gold)", color: "var(--lt-gold)", padding: "8px 16px", borderRadius: "var(--radius-md)", fontSize: 13, fontWeight: 600, cursor: "pointer", width: "100%" }}>
                  {"\uD83D\uDCCE \uC790\uACA9\uC99D \uC778\uC99D \uC2E0\uCCAD"}
                </button>
              )}
            </div>

            {/* 알림 이메일 */}
            <div style={styles.emailSection}>
              <div style={styles.emailHeader}>
                <span style={styles.emailLabel}>{"\uC54C\uB9BC \uC774\uBA54\uC77C"}</span>
                {!emailEdit && (
                  <button
                    onClick={() => { setEmailEdit(true); setEmailValue(broker.email || ""); setEmailMsg(""); }}
                    style={styles.emailEditBtn}
                  >
                    {broker.email ? "\uBCC0\uACBD" : "\uC124\uC815"}
                  </button>
                )}
              </div>
              {!emailEdit ? (
                <p style={styles.emailValue}>
                  {broker.email || "\uBBF8\uC124\uC815 (\uBB38\uC758 \uC54C\uB9BC\uC744 \uBC1B\uC73C\uB824\uBA74 \uC774\uBA54\uC77C\uC744 \uB4F1\uB85D\uD558\uC138\uC694)"}
                </p>
              ) : (
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <input
                    type="email"
                    value={emailValue}
                    onChange={(e) => setEmailValue(e.target.value)}
                    placeholder="example@email.com"
                    style={styles.emailInput}
                    autoFocus
                  />
                  <button onClick={handleEmailSave} disabled={emailSaving} style={styles.emailSaveBtn}>
                    {emailSaving ? "..." : "\uC800\uC7A5"}
                  </button>
                  <button onClick={() => { setEmailEdit(false); setEmailMsg(""); }} style={styles.emailCancelBtn}>
                    {"\uCDE8\uC18C"}
                  </button>
                </div>
              )}
              {emailMsg && (
                <p style={{ fontSize: 12, color: emailMsg.includes("\uC2E4\uD328") ? "var(--lt-red)" : "var(--lt-green)", margin: "4px 0 0" }}>
                  {emailMsg}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Section 2: NDA 상태 ──────────────── */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>NDA 상태</h3>
          <div style={styles.cardBody}>
            {loadingNda ? (
              <p style={styles.muted}>확인 중...</p>
            ) : nda?.agreed ? (
              <div style={styles.ndaAgreed}>
                <div style={styles.ndaCheck}>V</div>
                <div>
                  <p style={styles.ndaText}>NDA 동의 완료</p>
                  <p style={styles.ndaSub}>
                    {nda.agreedAt
                      ? `${new Date(nda.agreedAt).toLocaleDateString("ko-KR")} 동의`
                      : ""}
                    {nda.name ? ` (서명: ${nda.name})` : ""}
                  </p>
                </div>
              </div>
            ) : (
              <div style={styles.ndaNotAgreed}>
                <p style={styles.ndaNotText}>
                  NDA 미동의 상태입니다. 동의하면 다른 중개사의 정확한 매물 주소를 열람할 수 있습니다.
                </p>
                <Link to="/nda" style={styles.ndaBtn}>
                  NDA 동의하기
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* ── Section 3: 받은 문의 ──────────────── */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>
            {"\uBC1B\uC740 \uBB38\uC758"}
            {!loadingInquiries && ` (${inquiries.length}\uAC74)`}
            {!loadingInquiries && inquiries.filter((i) => !i.is_read).length > 0 && (
              <span style={styles.unreadBadge}>
                {inquiries.filter((i) => !i.is_read).length} {"\uC0C8 \uBB38\uC758"}
              </span>
            )}
          </h3>
          <div style={styles.cardBody}>
            {loadingInquiries ? (
              <p style={styles.muted}>{"\uBD88\uB7EC\uC624\uB294 \uC911..."}</p>
            ) : inquiries.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyText}>{"\uBC1B\uC740 \uBB38\uC758\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4"}</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 10 }}>
                {inquiries.map((inq) => (
                  <div
                    key={inq.id}
                    className="lt-item-card"
                    style={{
                      ...styles.listingCard,
                      borderLeft: inq.is_read ? "3px solid var(--border-light)" : "3px solid var(--lt-gold)",
                      opacity: inq.is_read ? 0.7 : 1,
                    }}
                    onClick={() => !inq.is_read && markRead(inq.id)}
                  >
                    <div style={styles.listingTop}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                        {inq.sender_name}
                      </span>
                      <span style={styles.listingDate}>
                        {new Date(inq.created_at).toLocaleDateString("ko-KR")}
                      </span>
                    </div>
                    <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 6px" }}>
                      {"\uB9E4\uBB3C: "}{inq.listing_title}
                    </p>
                    <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5, whiteSpace: "pre-wrap" as const }}>
                      {inq.message}
                    </p>
                    <div style={{ marginTop: 8 }}>
                      <a
                        href={`tel:${inq.sender_phone}`}
                        style={styles.inquiryCallBtn}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {"\uD83D\uDCDE "}{formatPhone(inq.sender_phone)}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Section 4: 관심매물 ──────────────── */}
        <div style={styles.card}>
          <h3 style={{ ...styles.cardTitle, fontSize: 22, letterSpacing: "-0.5px", textTransform: "none" as const }}>
            {"\uAD00\uC2EC\uB9E4\uBB3C"}
            {!loadingFavorites && (
              <span style={{ fontSize: 24, fontWeight: 800, color: "var(--lt-gold)", marginLeft: 10 }}>
                {favorites.length}<span style={{ fontSize: 16, fontWeight: 600 }}>{"\uAC74"}</span>
              </span>
            )}
          </h3>
          <div style={styles.cardBody}>
            {loadingFavorites ? (
              <p style={styles.muted}>{"\uBD88\uB7EC\uC624\uB294 \uC911..."}</p>
            ) : favorites.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyText}>{"\uC544\uC9C1 \uCC1C\uD55C \uB9E4\uBB3C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4"}</p>
                <button
                  onClick={() => navigate("/listings")}
                  style={styles.registerBtn}
                >
                  {"\uB9E4\uBB3C \uD0D0\uC0C9"}
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 10 }}>
                {favorites.map((l) => (
                  <Link
                    key={l.id}
                    to={`/listings/${l.id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <div className="lt-item-card" style={styles.listingCard}>
                      <div style={styles.listingTop}>
                        <span style={styles.typeBadge}>{l.property_type}</span>
                        <span style={styles.listingDate}>
                          {new Date(l.created_at).toLocaleDateString("ko-KR")}
                        </span>
                      </div>
                      <p style={styles.listingLocation}>
                        {l.region_sido} {l.region_sigungu} {l.region_eupmyeondong}
                      </p>
                      <div style={styles.listingBottom}>
                        <span style={styles.listingPrice}>
                          {formatPrice(l.price_manwon)}{"\uC6D0"}
                        </span>
                        <span style={styles.listingArea}>
                          {formatArea(l.area_pyeong)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Section 5: 내 등록 매물 ──────────────── */}
        <div style={{ ...styles.card, marginBottom: showMyMap ? 0 : 20 }}>
          {/* 헤더 — 목록/지도 탭 전환 */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid var(--border-light)", background: "rgba(212,168,83,0.04)" }}>
            <h3 style={{ fontSize: 22, fontWeight: 800, color: "var(--lt-gold)", margin: 0, letterSpacing: "-0.5px" }}>
              내 등록 매물
              {!loadingListings && (
                <span style={{ fontSize: 24, fontWeight: 800, color: "var(--lt-gold)", marginLeft: 10 }}>
                  {hasActiveFilter ? `${filteredListings.length}/` : ""}{listings.length}<span style={{ fontSize: 16, fontWeight: 600 }}>건</span>
                </span>
              )}
            </h3>
            {!loadingListings && listings.length > 0 && (
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={() => { setShowMyMap(false); scrollToContent(); }}
                  style={{ padding: "7px 18px", borderRadius: "var(--radius-sm)", border: `1px solid ${!showMyMap ? "var(--lt-gold)" : "var(--border-medium)"}`, background: !showMyMap ? "rgba(212,168,83,0.12)" : "transparent", color: !showMyMap ? "var(--lt-gold)" : "var(--text-secondary)", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                >
                  목록
                </button>
                <button
                  onClick={() => { setShowMyMap(true); scrollToContent(); }}
                  style={{ padding: "7px 18px", borderRadius: "var(--radius-sm)", border: `1px solid ${showMyMap ? "var(--lt-gold)" : "var(--border-medium)"}`, background: showMyMap ? "rgba(212,168,83,0.12)" : "transparent", color: showMyMap ? "var(--lt-gold)" : "var(--text-secondary)", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                >
                  지도
                </button>
              </div>
            )}
          </div>

          {/* ── 검색/필터 바 (목록·지도 공통) ── */}
          {!loadingListings && listings.length > 0 && (
            <div style={{ padding: "12px 20px 10px", borderBottom: "1px solid var(--border-light)" }}>
              {/* 키워드 검색 */}
              <input
                type="text"
                placeholder="지역·유형으로 검색..."
                value={filterKeyword}
                onChange={e => setFilterKeyword(e.target.value)}
                style={{ width: "100%", padding: "9px 14px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 16, outline: "none", boxSizing: "border-box" as const, marginBottom: 8 }}
              />
              {/* 필터 + 정렬 */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const, marginBottom: 8 }}>
                <select value={filterType} onChange={e => setFilterType(e.target.value)} style={fSelectStyle}>
                  <option value="">전체 유형</option>
                  {["농지","임야","상가","주택","묘터","공장","창고"].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={fSelectStyle}>
                  <option value="">전체 상태</option>
                  <option value="active">공개중</option>
                  <option value="sold">거래완료</option>
                  <option value="hidden">숨김</option>
                </select>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={fSelectStyle}>
                  <option value="newest">최신순</option>
                  <option value="price_asc">가격 낮은순</option>
                  <option value="price_desc">가격 높은순</option>
                  <option value="area_asc">면적 작은순</option>
                  <option value="area_desc">면적 큰순</option>
                </select>
                {hasActiveFilter && (
                  <button onClick={resetFilters} style={{ padding: "7px 14px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-light)", background: "transparent", color: "var(--text-muted)", fontSize: 14, cursor: "pointer" }}>
                    초기화
                  </button>
                )}
              </div>
              {/* 가격 범위 */}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="number" placeholder="최소가 (만원)" value={filterPriceMin} onChange={e => setFilterPriceMin(e.target.value)} style={{ flex: 1, padding: "7px 12px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 15, outline: "none" }} />
                <span style={{ color: "var(--text-muted)", fontSize: 14, flexShrink: 0 }}>~</span>
                <input type="number" placeholder="최대가 (만원)" value={filterPriceMax} onChange={e => setFilterPriceMax(e.target.value)} style={{ flex: 1, padding: "7px 12px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 15, outline: "none" }} />
              </div>
            </div>
          )}

          {/* 탭 스크롤 앵커 */}
          <div ref={contentRef} style={{ scrollMarginTop: 8 }} />

          {/* ── 목록 뷰 (지도 뷰일 때는 숨김) ── */}
          {!showMyMap && (
            <div style={styles.cardBody}>
              {loadingListings ? (
                <p style={styles.muted}>불러오는 중...</p>
              ) : listings.length === 0 ? (
                <div style={styles.emptyState}>
                  <p style={styles.emptyText}>아직 등록한 매물이 없습니다</p>
                  <Link to="/listings/new" style={styles.registerBtn}>매물 등록하기</Link>
                </div>
              ) : filteredListings.length === 0 ? (
                <p style={styles.muted}>검색 조건에 맞는 매물이 없습니다</p>
              ) : (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))",
                  gap: isMobile ? 12 : 16,
                }}>
                  {filteredListings.map((l) => {
                    const isDeleting = deletingId === l.id;
                    const isStatusLoading = statusLoadingId === l.id;
                    const statusInfo = l.status === "sold"
                      ? { label: "거래완료", color: "#3B82F6", bg: "rgba(59,130,246,0.12)" }
                      : l.status === "hidden"
                      ? { label: "숨김", color: "var(--text-muted)", bg: "rgba(100,116,139,0.12)" }
                      : { label: "공개중", color: "var(--lt-green)", bg: "rgba(16,185,129,0.12)" };
                    return (
                      <div key={l.id} style={{ ...styles.listingCard, borderLeft: `3px solid ${statusInfo.color}`, opacity: isDeleting ? 0.5 : 1 }}>
                        <div style={styles.listingTop}>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <span style={styles.typeBadge}>{l.property_type}</span>
                            <span style={{ ...styles.typeBadge, background: statusInfo.bg, color: statusInfo.color }}>{statusInfo.label}</span>
                          </div>
                          <span style={styles.listingDate}>{new Date(l.created_at).toLocaleDateString("ko-KR")}</span>
                        </div>
                        <p style={styles.listingLocation}>{l.region_sido} {l.region_sigungu} {l.region_eupmyeondong}</p>
                        <div style={styles.listingBottom}>
                          <span style={styles.listingPrice}>{formatPrice(l.price_manwon)}원</span>
                          <span style={styles.listingArea}>{formatArea(l.area_pyeong)}</span>
                        </div>
                        <div style={styles.actionRow}>
                          <button onClick={() => navigate(`/listings/${l.id}/edit`)} style={styles.actionBtnEdit} disabled={isDeleting || isStatusLoading}>수정</button>
                          {l.status === "active" && (
                            <>
                              <button onClick={() => handleStatusChange(l.id, "sold")} style={styles.actionBtnSold} disabled={isDeleting || isStatusLoading}>{isStatusLoading ? "..." : "거래완료"}</button>
                              <button onClick={() => handleStatusChange(l.id, "hidden")} style={styles.actionBtnHidden} disabled={isDeleting || isStatusLoading}>{isStatusLoading ? "..." : "숨김"}</button>
                            </>
                          )}
                          {(l.status === "sold" || l.status === "hidden") && (
                            <button onClick={() => handleStatusChange(l.id, "active")} style={styles.actionBtnActive} disabled={isDeleting || isStatusLoading}>{isStatusLoading ? "..." : "공개 전환"}</button>
                          )}
                          <button onClick={() => handleDelete(l.id)} style={styles.actionBtnDelete} disabled={isDeleting || isStatusLoading}>{isDeleting ? "..." : "삭제"}</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 지도 — container 안, 필터/카드와 동일 너비 */}
        {showMyMap && (
          <div id="mypage-map-wrap" style={{ position: 'relative' }}>
            <KakaoMapView
              active={true}
              updateKey={String(filteredListings.length) + filterKeyword + filterType + filterStatus}
              height="calc(100vh - 250px)"
              mapStyle={{ minHeight: 500, width: "100%", borderRadius: "var(--radius-lg)", overflow: "hidden" }}
              listings={filteredListings as unknown as ListingResponse[]}
              statusColorMode={true}
              onDetailClick={openDetail}
            />
            {/* 지도 위 투명 오버레이 — 버튼만 pointer-events 활성화 */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 99999 }}>
              <button
                onClick={() => {
                  const el = document.getElementById('mypage-map-wrap');
                  if (el) el.requestFullscreen();
                }}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  pointerEvents: 'auto',
                  background: 'rgba(0,0,0,0.8)',
                  border: '2px solid #D4AF37',
                  color: '#D4AF37',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                ⛶ 전체화면
              </button>
            </div>
          </div>
        )}
      </div>

      <AppFooter />

      {/* 지도 상세 패널 */}
      {selectedMapDetail && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: isMobile ? '100%' : 420,
          background: 'var(--bg-card)',
          borderLeft: '1px solid var(--border-light)',
          zIndex: 9999,
          overflowY: 'auto',
          boxShadow: '-4px 0 32px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* 헤더 */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: 'var(--bg-elevated)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ padding: '3px 10px', borderRadius: 10, background: selectedMapDetail.status === 'sold' ? '#3B82F6' : selectedMapDetail.status === 'hidden' ? '#6B7280' : '#FBBF24', color: '#fff', fontSize: 12, fontWeight: 700 }}>
                {selectedMapDetail.property_type}
              </span>
              <span style={{ fontSize: 12, color: selectedMapDetail.status === 'active' ? '#16A34A' : 'var(--text-muted)', fontWeight: 600 }}>
                {selectedMapDetail.status === 'active' ? '공개중' : selectedMapDetail.status === 'sold' ? '거래완료' : '숨김'}
              </span>
              {detailData?.broker_is_verified && <span style={{ fontSize: 11, color: '#16A34A', fontWeight: 700 }}>✓ 인증</span>}
            </div>
            <button onClick={() => { setSelectedMapDetail(null); setDetailData(null); }} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
          </div>

          {/* 사진 */}
          {detailData?.photos?.length > 0 && (
            <div style={{ display: 'flex', gap: 4, padding: '10px 12px', overflowX: 'auto', background: '#000', flexShrink: 0 }}>
              {detailData.photos.slice(0, 5).map((src: string, i: number) => (
                <img key={i} src={src} alt="" style={{ height: 90, minWidth: 120, objectFit: 'cover', borderRadius: 6 }} />
              ))}
            </div>
          )}

          {/* 로딩 */}
          {detailLoading && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>불러오는 중...</div>
          )}

          {/* 내용 */}
          <div style={{ padding: '16px', flex: 1 }}>
            {/* 가격 */}
            <div style={{ fontSize: 30, fontWeight: 800, color: '#FBBF24', marginBottom: 2 }}>
              {formatPrice(detailData ? detailData.price_manwon : selectedMapDetail.price_manwon)}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
              평당 {selectedMapDetail.area_pyeong > 0
                ? Math.round((detailData ? detailData.price_manwon : selectedMapDetail.price_manwon) / selectedMapDetail.area_pyeong).toLocaleString()
                : '-'}만원
            </div>

            {/* 위치 */}
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: detailData?.nda_agreed && detailData?.exact_address ? 6 : 14 }}>
              {selectedMapDetail.region_sido} {selectedMapDetail.region_sigungu} {selectedMapDetail.region_eupmyeondong}
            </div>
            {detailData?.nda_agreed && detailData?.exact_address && (
              <div style={{ fontSize: 13, color: '#16A34A', fontWeight: 600, marginBottom: 14, padding: '8px 10px', background: 'rgba(22,163,74,0.08)', borderRadius: 6, border: '1px solid rgba(22,163,74,0.2)' }}>
                {"📍 "}{detailData.exact_address}
              </div>
            )}

            {/* 정보 그리드 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 14 }}>
              {([
                ['면적', `${(detailData?.area_pyeong || selectedMapDetail.area_pyeong).toLocaleString()}평`],
                ['등록일', selectedMapDetail.created_at.slice(0, 10)],
                ['지목', detailData?.land_category || '-'],
                ['용도지역', detailData?.zoning || '-'],
                ['도로접면', detailData?.road_access || '-'],
                ['수도/용수', detailData?.water_supply || '-'],
              ] as [string, string][]).map(([label, value]) => (
                <div key={label} style={{ padding: '9px 11px', background: 'var(--bg-secondary)', borderRadius: 7, border: '1px solid var(--border-light)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
                </div>
              ))}
            </div>

            {/* 설명 */}
            {detailData?.description && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: 14, padding: '12px', background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border-light)' }}>
                {detailData.description}
              </div>
            )}

            {/* NDA 비공개 메모 */}
            {detailData?.nda_agreed && detailData?.detailed_notes && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: 14, padding: '12px', background: 'rgba(212,168,83,0.06)', borderRadius: 8, border: '1px solid rgba(212,168,83,0.2)' }}>
                <div style={{ fontSize: 11, color: '#D4AF37', fontWeight: 700, marginBottom: 6 }}>{"🔒 NDA 비공개 메모"}</div>
                {detailData.detailed_notes}
              </div>
            )}

            {/* 중개사 */}
            {detailData && (
              <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border-light)', marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>담당 중개사</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{detailData.broker_name}</div>
                {detailData.broker_office_name && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{detailData.broker_office_name}</div>}
              </div>
            )}

            {/* 추천 용도 */}
            {detailData?.recommended_use && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14, padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 8 }}>추천용도</span>
                {detailData.recommended_use}
              </div>
            )}
          </div>

          {/* 하단 버튼 */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-light)', flexShrink: 0 }}>
            <button
              onClick={() => { navigate(`/listings/${selectedMapDetail.id}/edit`); setSelectedMapDetail(null); setDetailData(null); }}
              style={{ width: '100%', padding: '12px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#B8972E,#D4AF37)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              수정하기
            </button>
          </div>
        </div>
      )}

      {/* 자격증 인증 서류 제출 모달 */}
      {showVerifyModal && (
        <div style={styles.modalOverlay} onClick={() => { setShowVerifyModal(false); setVerifyFiles([]); setVerifyMsg(""); }}>
          <div style={{ ...styles.modal, padding: isMobile ? 16 : 24, margin: isMobile ? "0 12px" : 0 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, color: "var(--text-primary)" }}>
              {"\uC790\uACA9\uC99D \uC778\uC99D \uC11C\uB958 \uC81C\uCD9C"}
            </h3>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
              {"\uC790\uACA9\uC99D \uB610\uB294 \uC778\uC99D \uC11C\uB958 \uC0AC\uC9C4\uC744 \uCD5C\uB300 3\uC7A5 \uC81C\uCD9C\uD574\uC8FC\uC138\uC694."}
              <br />
              {"(\uC7A5\uB2F9 \uCD5C\uB300 2MB, jpg/png/gif/webp)"}
            </p>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleVerifyFileSelect}
              style={{ display: "none" }}
              id="verify-file-input"
            />
            <label
              htmlFor="verify-file-input"
              style={{ display: "block", padding: "20px", border: "2px dashed var(--border-medium)", borderRadius: "var(--radius-md)", textAlign: "center" as const, cursor: "pointer", color: "var(--text-muted)", fontSize: 14, marginBottom: 12 }}
            >
              {verifyFiles.length === 0 ? "\uD074\uB9AD\uD558\uC5EC \uC0AC\uC9C4 \uC120\uD0DD" : `${verifyFiles.length}\uC7A5 \uC120\uD0DD\uB428 (\uCD5C\uB300 3\uC7A5)`}
            </label>
            {verifyFiles.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
                {verifyFiles.map((f, i) => (
                  <div key={i} style={{ position: "relative" as const }}>
                    <img src={f} alt={`\uC11C\uB958 ${i + 1}`} style={{ width: "100%", aspectRatio: "1", objectFit: "cover" as const, borderRadius: "var(--radius-sm)", border: "1px solid var(--border-light)" }} />
                    <button
                      onClick={() => setVerifyFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      style={{ position: "absolute" as const, top: 2, right: 2, background: "rgba(0,0,0,0.65)", border: "none", color: "#fff", borderRadius: "50%", width: 20, height: 20, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                    >
                      {"\u00D7"}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {verifyMsg && (
              <p style={{ fontSize: 13, color: verifyMsg.includes("\uC81C\uCD9C\uB418\uC5C8") ? "var(--lt-green)" : "var(--lt-red)", margin: "0 0 12px" }}>
                {verifyMsg}
              </p>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => { setShowVerifyModal(false); setVerifyFiles([]); setVerifyMsg(""); }} style={styles.emailCancelBtn}>
                {"\uCDE8\uC18C"}
              </button>
              <button
                onClick={handleVerifySubmit}
                disabled={verifyLoading || verifyFiles.length === 0}
                style={{ ...styles.emailSaveBtn, opacity: verifyFiles.length === 0 ? 0.5 : 1 }}
              >
                {verifyLoading ? "\uC81C\uCD9C \uC911..." : "\uC81C\uCD9C"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 내부 컴포넌트 ────────────────────────

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{label}</span>
      <span style={styles.infoValue}>{value}</span>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div style={styles.miniStat}>
      <p style={styles.miniStatValue}>{value}</p>
      <p style={styles.miniStatLabel}>{label}</p>
    </div>
  );
}

// ── 스타일 ──────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "var(--bg-primary)",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    color: "var(--text-muted)",
  },
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 32px 48px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 0",
    borderBottom: "1px solid var(--border-light)",
    marginBottom: 20,
  },
  backBtn: {
    color: "var(--text-muted)",
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 500,
    padding: "8px 0",
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: 0,
  },

  // ── 카드 공통 ──
  card: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderTop: "2px solid var(--lt-gold)",
    borderRadius: "var(--radius-xl)",
    overflow: "hidden",
    marginBottom: 20,
    boxShadow: "var(--shadow-md)",
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--lt-gold)",
    margin: 0,
    padding: "12px 20px",
    borderBottom: "1px solid var(--border-light)",
    letterSpacing: "0.3px",
    textTransform: "uppercase" as const,
    background: "rgba(212,168,83,0.04)",
  },
  cardBody: {
    padding: 20,
  },

  // ── 프로필 ──
  profileRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #B8972E, #D4AF37)",
    color: "#0F172A",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    fontWeight: 800,
    flexShrink: 0,
    boxShadow: "0 4px 12px rgba(212,168,83,0.3)",
  },
  name: {
    fontSize: 18,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: 0,
  },
  officeName: {
    fontSize: 13,
    color: "var(--text-muted)",
    margin: "2px 0 0",
  },
  verifiedBadge: {
    padding: "4px 10px",
    borderRadius: "var(--radius-sm)",
    background: "rgba(16, 185, 129, 0.15)",
    color: "var(--lt-green)",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap" as const,
  },
  pendingBadge: {
    padding: "4px 10px",
    borderRadius: "var(--radius-sm)",
    background: "rgba(245, 158, 11, 0.15)",
    color: "var(--lt-orange)",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap" as const,
  },
  rejectedBadge: {
    padding: "4px 10px",
    borderRadius: "var(--radius-sm)",
    background: "rgba(239, 68, 68, 0.15)",
    color: "var(--lt-red)",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap" as const,
  },
  unverifiedBadge: {
    padding: "4px 10px",
    borderRadius: "var(--radius-sm)",
    background: "rgba(100, 116, 139, 0.15)",
    color: "var(--text-muted)",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap" as const,
  },
  modalOverlay: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
  },
  modal: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderRadius: "var(--radius-xl)",
    padding: 24,
    width: "100%",
    maxWidth: 420,
  },

  // ── 정보 행 ──
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    background: "var(--bg-secondary)",
    borderRadius: "var(--radius-md)",
  },
  infoLabel: {
    fontSize: 13,
    color: "var(--text-muted)",
  },
  infoValue: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-primary)",
  },

  // ── 신뢰도 ──
  trustSection: {
    marginTop: 16,
    padding: "14px",
    background: "rgba(212,168,83,0.04)",
    border: "1px solid rgba(212,168,83,0.12)",
    borderRadius: "var(--radius-md)",
  },
  trustHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  trustLabel: {
    fontSize: 13,
    color: "var(--text-muted)",
  },
  progressBg: {
    width: "100%",
    height: 8,
    borderRadius: 4,
    background: "var(--border-light)",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 4,
    transition: "width 0.5s ease",
  },

  // ── 미니 통계 ──
  miniStat: {
    textAlign: "center" as const,
    padding: "14px 8px",
    background: "rgba(212,168,83,0.05)",
    border: "1px solid rgba(212,168,83,0.15)",
    borderRadius: "var(--radius-md)",
  },
  miniStatValue: {
    fontSize: 24,
    fontWeight: 800,
    color: "var(--lt-gold)",
    margin: 0,
  },
  miniStatLabel: {
    fontSize: 12,
    color: "var(--text-muted)",
    margin: "4px 0 0",
    letterSpacing: "0.4px",
    textTransform: "uppercase" as const,
  },

  // ── NDA ──
  muted: {
    color: "var(--text-muted)",
    fontSize: 15,
    margin: 0,
  },
  ndaAgreed: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  ndaCheck: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(16, 185, 129, 0.15)",
    color: "var(--lt-green)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 700,
    flexShrink: 0,
  },
  ndaText: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--lt-green)",
    margin: 0,
  },
  ndaSub: {
    fontSize: 12,
    color: "var(--text-muted)",
    margin: "2px 0 0",
  },
  ndaNotAgreed: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
  },
  ndaNotText: {
    fontSize: 13,
    color: "var(--text-secondary)",
    margin: 0,
    lineHeight: 1.5,
  },
  ndaBtn: {
    display: "inline-block",
    padding: "10px 20px",
    borderRadius: "var(--radius-md)",
    background: "var(--lt-gold)",
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    textAlign: "center" as const,
  },

  // ── 매물 리스트 ──
  emptyState: {
    textAlign: "center" as const,
    padding: "20px 0",
  },
  emptyText: {
    fontSize: 16,
    color: "#CBD5E1",
    margin: "0 0 12px",
  },
  registerBtn: {
    display: "inline-block",
    padding: "10px 20px",
    borderRadius: "var(--radius-md)",
    background: "var(--lt-gold)",
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
  },
  listingCard: {
    padding: "14px 16px",
    background: "var(--bg-secondary)",
    borderRadius: "var(--radius-lg)",
    border: "1px solid var(--border-light)",
    borderLeft: "3px solid var(--lt-gold)",
    cursor: "pointer",
    transition: "var(--transition-fast)",
  },
  listingTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  typeBadge: {
    padding: "3px 10px",
    borderRadius: "var(--radius-sm)",
    background: "rgba(212, 168, 83, 0.15)",
    color: "var(--lt-gold)",
    fontSize: 13,
    fontWeight: 600,
  },
  listingDate: {
    fontSize: 13,
    color: "var(--text-muted)",
  },
  listingLocation: {
    fontSize: 15,
    color: "var(--text-primary)",
    margin: "0 0 8px",
    fontWeight: 500,
  },
  listingBottom: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  listingPrice: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--lt-gold)",
  },
  listingArea: {
    fontSize: 15,
    color: "var(--text-muted)",
  },

  // ── 알림 이메일 ──
  emailSection: {
    marginTop: 16,
    padding: "14px",
    background: "rgba(212,168,83,0.04)",
    border: "1px solid rgba(212,168,83,0.12)",
    borderRadius: "var(--radius-md)",
  },
  emailHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  emailLabel: {
    fontSize: 12,
    color: "var(--text-muted)",
    fontWeight: 600,
    letterSpacing: "0.3px",
    textTransform: "uppercase" as const,
  },
  emailEditBtn: {
    padding: "3px 10px",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--lt-gold)",
    background: "transparent",
    color: "var(--lt-gold)",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
  },
  emailValue: {
    fontSize: 13,
    color: "var(--text-secondary)",
    margin: 0,
  },
  emailInput: {
    flex: 1,
    padding: "8px 10px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontSize: 13,
    outline: "none",
  },
  emailSaveBtn: {
    padding: "8px 14px",
    borderRadius: "var(--radius-md)",
    border: "none",
    background: "var(--lt-gold)",
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  },
  emailCancelBtn: {
    padding: "8px 10px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-light)",
    background: "transparent",
    color: "var(--text-muted)",
    fontSize: 12,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  },

  // ── 매물 액션 버튼 ──
  actionRow: {
    display: "flex",
    gap: 6,
    marginTop: 10,
    flexWrap: "wrap" as const,
  },
  actionBtnEdit: {
    padding: "6px 14px",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--lt-gold)",
    background: "transparent",
    color: "var(--lt-gold)",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  actionBtnSold: {
    padding: "6px 14px",
    borderRadius: "var(--radius-sm)",
    border: "none",
    background: "rgba(59,130,246,0.15)",
    color: "#3B82F6",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  actionBtnHidden: {
    padding: "6px 14px",
    borderRadius: "var(--radius-sm)",
    border: "none",
    background: "rgba(100,116,139,0.15)",
    color: "var(--text-muted)",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  actionBtnActive: {
    padding: "6px 14px",
    borderRadius: "var(--radius-sm)",
    border: "none",
    background: "rgba(16,185,129,0.15)",
    color: "var(--lt-green)",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  actionBtnDelete: {
    padding: "6px 14px",
    borderRadius: "var(--radius-sm)",
    border: "none",
    background: "rgba(239,68,68,0.12)",
    color: "var(--lt-red)",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    marginLeft: "auto",
  },

  // ── 문의 ──
  unreadBadge: {
    marginLeft: 8,
    padding: "2px 8px",
    borderRadius: 10,
    background: "rgba(212, 168, 83, 0.2)",
    color: "var(--lt-gold)",
    fontSize: 11,
    fontWeight: 600,
  },
  inquiryCallBtn: {
    display: "inline-block",
    padding: "6px 14px",
    borderRadius: 8,
    background: "rgba(22, 163, 74, 0.15)",
    color: "#16A34A",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
  },
};
